import React, { useState, useEffect } from 'react';
import {
  Search, Plus, MapPin, User, LogOut, Menu, X, Check, Bell
} from 'lucide-react';
import AsyncSelect from "react-select/async";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import images from '../constants/images';
import '../styles/GlobalStyles.css';

const InstitutionNavbar = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPopUpOpen, setIsPopUpOpen] = useState(false);
  const [isHiringFor, setIsHiringFor] = useState('');
  const [selectedSearchOption, setSelectedSearchOption] = useState('');
  const [jobTitleSearch, setJobTitleSearch] = useState(''); // Stores the Title String
  const [selectedOptionData, setSelectedOptionData] = useState(null); // Stores the full option object
  const [loading, setLoading] = useState(false);
  const [institutionName, setInstitutionName] = useState('');
  const [institutionID, setInstitutionID] = useState('');
  const [notificationCount, setNotificationCount] = useState(0);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notificationDetails, setNotificationDetails] = useState([]);
  const [myJobs, setMyJobs] = useState([]);

  const navigate = useNavigate();

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const togglePopUp = () => {
    setIsPopUpOpen(!isPopUpOpen);
  };

  const handleHiringForChange = (e) => setIsHiringFor(e.target.value);

  const handleSearchOptionChange = async (option) => {
    if (option === 'Yes') {
      let currentJobs = myJobs;

      if (currentJobs.length === 0) {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/institutionfilter/getMyJobs`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.data && res.data.data) {
            currentJobs = res.data.data;
            setMyJobs(currentJobs);
          }
        } catch (err) {
          console.error("Error fetching my jobs:", err);
        }
      }

      if (currentJobs.length === 0) {
        alert("Please post a job to search relevent candidates.");
        return;
      }
    }

    setSelectedSearchOption(option);
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        localStorage.clear();
        window.location.href = '/login';
        return;
      }
      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/logoutUser`,
        { refreshToken },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      localStorage.clear();
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
      localStorage.clear();
      window.location.href = '/login';
    }
  };


  const handleCandidateSearch = async () => {
    try {
      setLoading(true);
      if (!institutionID) {
        alert("Institution ID is missing. Please wait or refresh the page.");
        return;
      }

      if (selectedSearchOption === 'Yes') {
        if (!selectedOptionData || !selectedOptionData.value) {
          alert("Please select a job from the list.");
          setLoading(false);
          return;
        }

        const payload = {
          jobId: selectedOptionData.value, // This is the JOB ID
          institutionId: institutionID
        };

        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/searchcandidate/search-Candidate`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (
          response.status === 200 &&
          response.data.candidates &&
          Array.isArray(response.data.candidates) &&
          response.data.candidates.length > 0
        ) {
          navigate('/candidate-results', { state: { candidates: response.data.candidates } });
        } else {
          alert("No candidates found matching this job criteria.");
        }

        setLoading(false);
        return;
      }

      // Existing fallback logic (Global Search)
      const payload = {
        position: jobTitleSearch,
        institutionId: institutionID
      };

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/searchcandidate/search-Candidate`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log("Search Payload:", payload);
      console.log("API Response:", response.data);

      if (
        response.data.candidates.length > 0
      ) {
        // --- Enhanced Prefill Logic for Global Search ---
        let subjectPrefill = [];

        // Extract subjects from the ACTUAL candidates returned
        const foundSubjects = new Set();
        response.data.candidates.forEach(candidate => {
          const wp = candidate.work_preference;
          // Check if candidate is looking for Teaching roles
          if (wp && (wp.category === 'Teaching' || wp.role_teaching)) {
            // 'subject' can be an array based on the user's description.
            // Also handling if it's a single string just in case, though user said "subject (array)"
            if (Array.isArray(wp.subject)) {
              wp.subject.forEach(sub => {
                if (sub) foundSubjects.add(sub);
              });
            } else if (typeof wp.subject === 'string' && wp.subject) {
              foundSubjects.add(wp.subject);
            }
          }
        });

        subjectPrefill = Array.from(foundSubjects);

        navigate('/candidate-results', {
          state: {
            candidates: response.data.candidates,
            initialFilters: {
              skill: subjectPrefill
            }
          }
        });
      } else {
        alert("No candidates found for this search.");
      }

    } catch (err) {
      alert('Failed to fetch candidates. Please try again.');
      console.error('Candidate Search Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [isMobileMenuOpen]);


  useEffect(() => {
    const fetchInstitutionProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/profile/getInstProfile`,
          { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
        );
        const name = response.data?.Institute?.name;
        const id = response.data?.Institute?._id;
        if (name) setInstitutionName(name);
        if (id) setInstitutionID(id);
      } catch (err) {
        console.error('Error fetching institution profile:', err);
      }
    };
    fetchInstitutionProfile();
  }, []);

  useEffect(() => {
    if (!institutionID) return;
    console.log("InstitutionID :-", institutionID);
    const fetchNotificationCount = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/notification/unseen-applications-count?institutionId=${institutionID}`,
          { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
        );
        setNotificationCount(res.data.count || 0);
      } catch (err) {
        console.error("Failed to fetch notification count:", err);
      }
    };
    fetchNotificationCount();
  }, [institutionID]);

  const toggleNotificationModal = async () => {
    setIsNotificationModalOpen(!isNotificationModalOpen);
    if (!isNotificationModalOpen) {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/notification/applications?institutionId=${institutionID}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          }
        );
        //console.log('Notification Data:', res.data); 
        setNotificationDetails(res.data || []);
      } catch (err) {
        console.error('Failed to fetch notification details:', err);
      }
    }

  };

  const handlePostJobClick = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/profile/getInstProfile`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data && res.data.Institute) {
        const { name, type, address } = res.data.Institute;
        // Check strict address
        const isBengaluru = address?.city?.toLowerCase().includes('bengaluru') || address?.city?.toLowerCase().includes('bangalore');

        const complete = name && type && address &&
          address.street &&
          address.city &&
          address.state &&
          address.postal_code &&
          address.country &&
          (!isBengaluru || address.area);

        if (complete) {
          navigate('/post-job');
        } else {
          if (window.confirm("Update your profile before posting a job.\n(Name, Type, and Address fields are required)\n\nClick OK to Edit Profile.")) {
            navigate('/InstitutionProfile');
          }
        }
      } else {
        // Fallback if no data
        navigate('/post-job');
      }
    } catch (err) {
      console.error("Error validating profile:", err);
      // Determine what to do on error. Allow or block? 
      // Safe to allow and let post-job page handle it, or block.
      // If API fails, maybe network error. navigate to post-job and let it fail there or handle it.
      // Better: navigate('/post-job') (the page has its own check I added earlier).
      navigate('/post-job');
    } finally {
      setLoading(false);
    }
  };
  const handlePromoteWalkinClick = () => {
    navigate("/institute/walkin-post");
  };




  return (
    <div className="font-sans bg-gray-50">
      <header className="fixed top-0 left-0 w-full z-50 bg-white/90 backdrop-blur-md shadow-md border-b border-gray-100">
        <nav className="w-full mx-auto px-4 md:px-8 py-3 flex items-center justify-between flex-nowrap">

          <div className="flex items-center shrink-0">

            <img
              src={images.jobLogo}
              className="h-16 w-auto object-contain rounded-lg"
              alt="Jobseeker Logo"
            />
          </div>

          <div className="hidden md:flex items-center space-x-6 flex-nowrap whitespace-nowrap">

            <a href="/InstitutionHomepage" className="text-base text-gray-800 transition hover:text-blue-700">Home</a>
            <a href="/manage-jobs" className="text-base text-gray-800 transition hover:text-blue-700">Manage Jobs</a>
            <a href="/manage-walkins" className="text-base text-gray-800 transition hover:text-blue-700">Manage Walk-ins</a>
            <a href="/ContactUs" className="text-base text-gray-800 transition hover:text-blue-700">Contact Us</a>
            {/* <a href="/InstitutionHomepage" className="text-base text-gray-800 transition hover:text-blue-700">FAQ's</a> */}

            <button onClick={togglePopUp} className="flex items-center px-4 py-2 text-sm text-blue-700 transition border-2 border-blue-100 rounded-lg hover:border-blue-600 hover:text-blue-600">
              <Search className="w-4 h-4 mr-2" /> Find a Candidate
            </button>

            <button
              onClick={handlePostJobClick}
              disabled={loading}
              className="primary-btn flex items-center !px-4 !py-2 !text-sm !rounded-xl shadow-lg hover:shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4 mr-2" />
              {loading ? 'Checking...' : 'Post New Job'}
            </button>

            <button
              onClick={handlePromoteWalkinClick}
              className="flex items-center px-4 py-2 text-sm text-gray-700 transition border-2 border-gray-100 rounded-lg hover:border-gray-300 hover:text-gray-900 secondary-btn"
            >
              <MapPin className="w-4 h-4 mr-2" /> Promote Walk Ins
            </button>


            <button onClick={toggleNotificationModal} className="relative p-2 text-gray-600 transition-colors rounded-full hover:bg-gray-100 hover:text-blue-600">
              <Bell className="w-6 h-6" />
              {notificationCount > 0 && (
                <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs text-white bg-red-600 border-2 border-white rounded-full">
                  {notificationCount}
                </span>
              )}
            </button>

            <div className="relative">
              <button className="flex items-center px-4 py-2 text-gray-700 transition rounded-lg hover:bg-gray-100 hover:text-blue-700" onClick={toggleDropdown}>
                <User className="w-5 h-5 mr-2 rounded-full bg-gray-200/50 p-0.5" />
                {institutionName || 'Institution'}
              </button>
              {isDropdownOpen && (
                <div className="absolute right-0 z-10 w-48 mt-2 bg-white border rounded-md shadow-lg">
                  <a href="/InstitutionProfile" className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100">
                    <User className="mr-2" /> My Profile
                  </a>

                  <button onClick={handleLogout} className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100">
                    <LogOut className="mr-2" /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center md:hidden">
            <button onClick={toggleMobileMenu}>
              {isMobileMenuOpen ? <X className="w-6 h-6 text-gray-700" /> : <Menu className="w-6 h-6 text-gray-700" />}
            </button>
          </div>
        </nav>




      </header>
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Drawer */}
          <div
            className={`
    fixed top-0 left-0 h-screen
    w-[180px]
    bg-white
    shadow-2xl
    z-50
    flex flex-col
    transform transition-transform duration-300 ease-out
    ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"}
  `}
          >


            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-300">

              <img
                src={images.jobLogo}
                alt="Logo"
                className="h-8 object-contain"

              />
              <button onClick={() => setIsMobileMenuOpen(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">

              <a
                href="/InstitutionHomepage"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-base  text-gray-800"
              >
                Home
              </a>

              <a
                href="/manage-jobs"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-base  text-gray-800"
              >
                Manage Jobs
              </a>

              <a
                href="/manage-walkins"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-base  text-gray-800"
              >
                Manage Walk-ins
              </a>

              <a
                href="/ContactUs"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-base  text-gray-800"
              >
                Contact Us
              </a>

              {/* <a
                href="/InstitutionHomepage"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-base font-bold text-gray-800"
              >
                FAQ's
              </a> */}

              <button
                onClick={() => {
                  togglePopUp();
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center px-4 py-3 text-blue-700 border rounded-xl"
              >
                <Search className="mr-3" />
                Find a Candidate
              </button>

              <button
                onClick={() => {
                  navigate('/post-job');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center px-4 py-3  text-white rounded-xl bg-blue-600"
              >
                <Plus className="mr-3" />
                Post New Job
              </button>

              <button className="w-full flex items-center px-4 py-3  border rounded-xl">
                <MapPin className="mr-3" />
                Promote Walk Ins
              </button>

              {/* Notifications */}
              <button
                onClick={() => {
                  toggleNotificationModal();
                  setIsMobileMenuOpen(false);
                }}
                className="flex items-center w-full "
              >
                <Bell className="mr-3" />
                Notifications
                {notificationCount > 0 && (
                  <span className="ml-auto px-2 py-0.5 text-xs text-white bg-red-600 rounded-full">
                    {notificationCount}
                  </span>
                )}
              </button>

              {/* Profile */}
              <div className="pt-4 border-t">
                <button
                  onClick={() => navigate('/InstitutionProfile')}
                  className="flex items-center w-full "
                >
                  <User className="mr-3" />
                  {institutionName || 'Institution'}
                </button>




                <button
                  onClick={handleLogout}
                  className="flex items-center w-full mt-3  text-red-600"
                >
                  <LogOut className="mr-3" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {isPopUpOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative p-6 bg-white rounded-md shadow-lg w-[50rem] max-h-[90vh] overflow-auto">
            <button className="absolute text-gray-700 top-2 right-2" onClick={togglePopUp}>
              <X />
            </button>
            <h2 className="mb-4 text-xl font-semibold text-center">Find a Candidate</h2>

            <div className="mb-4">
              <label className="block text-gray-700">Hiring For</label>
              <select
                className="w-full p-2 border rounded-md"
                value={isHiringFor}
                onChange={handleHiringForChange}
              >
                <option value="">Select an option</option>
                {institutionName && <option value={institutionName}>{institutionName}</option>}
              </select>
            </div>

            <div className="mb-4">
              <span className="text-gray-700">Search for existing jobs?</span>
              <div className="flex justify-center mt-2 space-x-4">
                {['Yes', 'No'].map((opt) => (
                  <button
                    key={opt}
                    className={`flex items-center p-2 font-semibold rounded-md ${selectedSearchOption === opt ? 'bg-blue-500 text-white' : 'text-blue-700 border'}`}
                    onClick={() => handleSearchOptionChange(opt)}
                  >
                    {selectedSearchOption === opt && <Check className="mr-2" />} {opt}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700">Select by Job Title</label>
              <AsyncSelect
                key={selectedSearchOption}
                cacheOptions
                defaultOptions
                loadOptions={async (inputValue) => {
                  if (selectedSearchOption === 'Yes') {
                    // ----------------------------------------------------------------
                    // MODE 1: Job Selection (Search by My Jobs)
                    // ----------------------------------------------------------------
                    // List Institution's Actual Jobs
                    let filteredJobs = myJobs || [];
                    if (inputValue) {
                      filteredJobs = filteredJobs.filter(job =>
                        job.title.toLowerCase().includes(inputValue.toLowerCase())
                      );
                    }

                    return filteredJobs.map(job => {
                      return {
                        value: job._id, // Send JOB ID
                        label: job.title,
                        type: 'JOB_SELECTION',
                        data: job
                      };
                    });

                  } else {
                    // ----------------------------------------------------------------
                    // MODE 2: Global Search (Autocomplete Titles)
                    // ----------------------------------------------------------------
                    if (!inputValue || inputValue.length < 2) return [];
                    try {
                      // Use the autocomplete endpoint (which should be just global search now)
                      // We can pass institutionId if we want "smart" global search, or just query
                      const token = localStorage.getItem('token');
                      const res = await axios.get(
                        `${process.env.REACT_APP_API_URL}/api/searchcandidate/autocomplete-titles?query=${inputValue}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                      );

                      return res.data.map((item) => ({ value: item.title, label: item.title }));
                    } catch (error) {
                      console.error("Error fetching job titles:", error);
                      return [];
                    }
                  }
                }}
                onChange={(selectedOption) => {
                  // If YES mode: store full option (which has value=jobId)
                  // If NO mode : store value (string)
                  if (!selectedOption) {
                    setJobTitleSearch("");
                    setSelectedOptionData(null);
                    return;
                  }

                  if (selectedSearchOption === 'Yes') {
                    // For display purposes, we might want to keep the label or just the title?
                    // Let's store the ID in a separate state if needed, or just rely on selectedOptionData
                    setJobTitleSearch(selectedOption.label); // Display text
                    setSelectedOptionData(selectedOption); // Contains value: jobId
                  } else {
                    setJobTitleSearch(selectedOption.value);
                    setSelectedOptionData(selectedOption);
                  }
                }}
                placeholder={selectedSearchOption === 'Yes' ? "Select from your jobs..." : "Type to search job title..."}
                isClearable
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#d1d5db",
                    boxShadow: "none",
                    "&:hover": { borderColor: "#3b82f6" },
                  }),
                }}
              />
            </div>

            <div className="flex justify-center mb-4">
              <button
                className="w-[20rem] p-2 font-semibold text-white bg-blue-500 rounded-md hover:bg-blue-600"
                onClick={handleCandidateSearch}
              >
                Find a Candidate
              </button>
            </div>

            <button className="absolute text-gray-700 top-2 right-2" onClick={togglePopUp}>
              <X />
            </button>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {isNotificationModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative p-6 bg-white rounded-md shadow-lg w-[40rem] max-h-[80vh] overflow-y-auto">
            <button className="absolute text-gray-700 top-2 right-2" onClick={toggleNotificationModal}>
              <X />
            </button>
            <h2 className="mb-4 text-xl font-semibold text-center">Notifications</h2>
            {notificationDetails.length === 0 ? (
              <p className="text-center text-gray-500">No new notifications.</p>
            ) : (
              <ul className="space-y-3">
                {notificationDetails.map((notif, index) => (
                  <li key={index} className="flex flex-col p-4 bg-white border border-gray-100 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-gray-800">
                          <span className="font-bold text-blue-600">{notif.applicantName}</span> applied for <span className="font-semibold text-gray-700">{notif.jobTitle}</span>
                        </p>
                        <div className="flex items-center mt-1 text-xs text-gray-500">
                          <MapPin className="w-3 h-3 mr-1" />
                          {notif.location || "Location not available"}
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            await axios.post(
                              `${process.env.REACT_APP_API_URL}/api/notification/mark-one-seen/${notif.applicationId}`,
                              {},
                              { headers: { Authorization: `Bearer ${token}` } }
                            );

                            // Update local state immediately
                            const updatedDetails = notificationDetails.filter(n => n.applicationId !== notif.applicationId);
                            setNotificationDetails(updatedDetails);
                            setNotificationCount(prev => Math.max(0, prev - 1));

                            // Navigate
                            navigate(`/candidate-details/${notif.candidateId}`);

                            // Close if empty
                            if (updatedDetails.length === 0) {
                              setIsNotificationModalOpen(false);
                            }
                          } catch (err) {
                            console.error("Failed to mark notification seen:", err);
                            // Still navigate even if mark-seen fails
                            navigate(`/candidate-details/${notif.candidateId}`);
                            setIsNotificationModalOpen(false);
                          }
                        }}
                        className="px-3 py-1.5 ml-3 text-xs font-bold text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors shrink-0"
                      >
                        View Profile
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-gray-400 text-right">
                      {new Date(notif.dateApplied).toLocaleDateString()}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InstitutionNavbar;